import { useSelector } from "react-redux";
import { createSelector } from "reselect";

import { RootState } from "store";
import LikeableType from "types/LikeableType";
import { PromptSerializersPromptBook } from "types/serializers";
import UserType, { LibrarianType } from "types/UserType";

const getState = (state: RootState) => state;

export const getCurrentUserChannel = createSelector(
  getState,
  state => state.currentUser,
);

export const getSession = createSelector(
  getCurrentUserChannel,
  ({ session }) => {
    return session;
  },
);

export const getUserId = createSelector(getCurrentUserChannel, ({ user }) => {
  return user?.id || null;
});

export type CurrentUserFilter = (user: UserType) => any;
export const getUserWithFilter: (state: any, filter: CurrentUserFilter) => any =
  createSelector(
    getCurrentUserChannel,
    (_, filter: CurrentUserFilter) => filter,
    ({ user }, filter: CurrentUserFilter) => {
      return filter(user || null);
    },
  );

// Match Score
// Get the currently logged in user's book
export const getMatchScore = (bookId: number) =>
  createSelector(
    getCurrentUserChannel,
    ({ matchScores }): number | null | undefined => {
      return matchScores ? matchScores[bookId] : null;
    },
  );
export function useUserBookMatchScore(
  bookId: number,
): number | null | undefined {
  return useSelector(getMatchScore(bookId));
}

// Likes
export function likeableKey(likeable: LikeableType): string {
  return `${likeable.objectType.toLowerCase()}-${likeable.id}`;
}
// Get the currently logged in user's book
export const getLike = (key: string) =>
  createSelector(getCurrentUserChannel, ({ likes }): boolean => {
    return likes ? likes[key] || false : false;
  });
export function useIsLiked(likeable: LikeableType): boolean {
  return useSelector(getLike(likeableKey(likeable)));
}

// Owned Editions
// Get the currently logged in user's book
export const getEditionOwnershipStatus = (editionId: number) =>
  createSelector(getCurrentUserChannel, ({ ownedEditions }): number | null => {
    return ownedEditions ? ownedEditions[editionId] || null : null;
  });
export function useIsEditionOwnedId(editionId: number): number {
  return useSelector(getEditionOwnershipStatus(editionId));
}

// Test if the current user has a specific role
export const getCurrentUserHasRole = (role: LibrarianType) =>
  createSelector(getCurrentUserChannel, ({ user }): boolean => {
    if (!user) {
      return false;
    }
    return user.librarianRoles.includes(role);
  });
export function useCurrentUserRole(role: LibrarianType): boolean {
  return useSelector(getCurrentUserHasRole(role));
}

// Get all of the current users librarian roles
export const getCurrentUserRoles = () =>
  createSelector(getCurrentUserChannel, ({ user }): LibrarianType[] => {
    if (!user) {
      return [];
    }
    return user.librarianRoles;
  });
export function useCurrentUserRoles(): LibrarianType[] {
  return useSelector(getCurrentUserRoles());
}

// Prompt Vote
export const getPromptAnswer = (promptId: number, bookId: number) =>
  createSelector(getCurrentUserChannel, ({ promptAnswers }): number | null => {
    return promptAnswers
      ? promptAnswers[`Prompt-${promptId}-${bookId}`] || null
      : null;
  });
export function usePromptAnswer(
  promptBook: PromptSerializersPromptBook,
): number {
  return useSelector(getPromptAnswer(promptBook.promptId, promptBook.bookId));
}

// Pinned Prompts
export const getPinnedPrompt = (promptId: number) =>
  createSelector(getCurrentUserChannel, ({ pinnedPromptIds }): boolean => {
    return pinnedPromptIds ? pinnedPromptIds.includes(promptId) : false;
  });
export function useIsPromptPinned(promptId: number): boolean {
  return useSelector(getPinnedPrompt(promptId));
}

// Followed Users
export const getFollowedUser = (userId: number) =>
  createSelector(getCurrentUserChannel, ({ followedUsers }): boolean => {
    const followingBit = followedUsers ? followedUsers[userId] || 0 : 0;
    return followingBit === 1 || followingBit === 7; // Follows, or both follows
  });
export function useIsFollowing(userId: number): boolean {
  return useSelector(getFollowedUser(userId));
}

export const getFollowingUser = (userId: number) =>
  createSelector(getCurrentUserChannel, ({ followedUsers }): boolean => {
    const followingBit = followedUsers ? followedUsers[userId] || 0 : 0;
    return followingBit === 2 || followingBit === 7; // Follows, or both follows
  });
export function useIsFollowedBy(userId: number): boolean {
  return useSelector(getFollowingUser(userId));
}
